import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '@/components/Layout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { LogOut } from 'lucide-react';
import axios from 'axios';
import LeadCard from '@/components/admin/LeadCard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminLeads() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(null);
  const navigate = useNavigate();

  // Vérifier l'authentification au chargement
  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (!token) {
      navigate('/admin/login');
    }
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminUser');
    toast.success('Déconnexion réussie');
    navigate('/admin/login');
  };

  const fetchLeads = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/leads`);
      setLeads(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des leads');
    } finally {
      setLoading(false);
    }
  }, []);

  const updateLeadStatus = async (leadId, newStatus) => {
    setUpdating(leadId);
    try {
      await axios.patch(`${API}/leads/${leadId}`, { status: newStatus });
      toast.success('Statut mis à jour');
      fetchLeads();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    } finally {
      setUpdating(null);
    }
  };

  useEffect(() => {
    fetchLeads();
  }, [fetchLeads]);

  return (
    <Layout>
      <div className="min-h-screen bg-[#F5F3EF] py-12 px-6">
        <div className="max-w-7xl mx-auto">
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold text-[#2B2B2B] mb-2" data-testid="admin-title">
              Gestion des Leads
            </h1>
            <p className="text-[#8B8B8B]">
              {leads.length} demande{leads.length !== 1 ? 's' : ''} d'estimation
            </p>
          </div>
          <Button
            onClick={handleLogout}
            variant="outline"
            className="border-[#8A9474] text-[#8A9474] hover:bg-[#8A9474] hover:text-white"
          >
            <LogOut className="h-4 w-4 mr-2" />
            Déconnexion
          </Button>
        </div>

        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3].map(i => (
              <Card key={i}>
                <CardContent className="p-6">
                  <Skeleton className="h-8 w-3/4 mb-4" />
                  <Skeleton className="h-4 w-full mb-2" />
                  <Skeleton className="h-4 w-2/3" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : leads.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <p className="text-[#8B8B8B]" data-testid="admin-empty-state">
                Aucun lead pour le moment
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {leads.map((lead) => (
              <LeadCard
                key={lead.id}
                lead={lead}
                onStatusChange={updateLeadStatus}
                updating={updating}
              />
            ))}
          </div>
        )}
      </div>
    </div>
    </Layout>
  );
}
